import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelDocumentTypesApi } from '../personnelDocumentTypesApi';
import type { PersonnelDocumentType } from '../personnelDocumentTypes';
import { samplePersonnelDocumentType } from '../utils/samplePersonnelDocumentType';

/**
 * Create or edit one row of the document checklist.
 *
 * Two fields, and no position: where a row sits on the checklist is assigned by the server and
 * changed by moving the row, not by typing a number here — a rename is not a reorder.
 */
export function PersonnelDocumentTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: PersonnelDocumentType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (editing) await personnelDocumentTypesApi.update(editing.id, request);
      else await personnelDocumentTypesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'personnel-document-type' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = samplePersonnelDocumentType();

    setName(sample.name);
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit document' : 'New document'}
      size="lg"
      id="personnel-document-type-modal"
      autogenerate={editing === null ? { onGenerate: generate, disabled: saving } : undefined}
      confirm={{
        id: 'personnel-document-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="personnel-document-type-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="personnel-document-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="personnel-document-type-name"
          label="Name"
          required
          description="What the checklist row is called — Certificate of Live Birth, Diploma, Appointment."
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
          disabled={saving}
        />

        <TextField
          id="personnel-document-type-description"
          label="Description"
          description="One line saying what is expected. Shown under the row on every 201 file."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        {/*
          Said on the form rather than on the tick: a new row appears on every 201 file's
          Documents panel from the moment it is saved, which is a change to what the whole
          module asks for.
        */}
        <p className="text-xs text-slate-500">
          A new document becomes a row on the Documents panel of every 201 file. Deleting one
          takes the row off the checklist and leaves the documents already filed against it
          alone.
        </p>
      </div>
    </Modal>
  );
}
